import { ERROR_CODES } from "../constants/errorCodes";
import type { CourseCertificateUploadError } from "../models/courseCertificate";
import type { UploadChunk } from "./planCourseCertificateUpload";

const NUMERIC_ID = /^\d+$/;

/** The API sets each error's id to the file's index *within its own request*,
 * so every chunk hands back ids "0".."4". Left alone they collide across
 * chunks and React drops rows. Rewriting them to the selection index makes
 * them unique and meaningful. */
const resolveSelectionId = (
  chunk: UploadChunk,
  error: CourseCertificateUploadError,
  position: number,
): string => {
  if (NUMERIC_ID.test(error.id)) {
    const entry = chunk.entries[Number(error.id)];

    if (entry) {
      return String(entry.selectionIndex);
    }
  }

  const byFileName = chunk.entries.find(
    (entry) => entry.file.name === error.fileName,
  );

  if (byFileName) {
    return String(byFileName.selectionIndex);
  }

  return `${chunk.chunkIndex}-${position}`;
};

export const remapChunkErrors = (
  chunk: UploadChunk,
  errors: readonly CourseCertificateUploadError[],
): CourseCertificateUploadError[] =>
  errors.map((error, position) => ({
    ...error,
    id: resolveSelectionId(chunk, error, position),
  }));

/** A chunk that failed outright tells us nothing per file, so every file in it
 * becomes an UPLOAD_FAILED row. That keeps one error list instead of a
 * separate "the request failed" banner, and still names the files to re-pick. */
export const chunkFailureToUploadErrors = (
  chunk: UploadChunk,
): CourseCertificateUploadError[] =>
  chunk.entries.map((entry) => ({
    id: String(entry.selectionIndex),
    fileName: entry.file.name,
    errorCode: ERROR_CODES.UPLOAD_FAILED,
  }));

const sortKey = (error: CourseCertificateUploadError): number =>
  NUMERIC_ID.test(error.id) ? Number(error.id) : Number.POSITIVE_INFINITY;

/** Restores the user's original pick order across chunks that finished out of
 * order. Ids that are not selection indices (the fallback case) sort last. */
export const sortUploadErrorsBySelectionIndex = (
  errors: readonly CourseCertificateUploadError[],
): CourseCertificateUploadError[] =>
  [...errors].sort((left, right) => sortKey(left) - sortKey(right));
